/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * Normalize product main images (max 3) from API payloads and form state.
 */
export const MAX_MAIN_IMAGES = 3;

export const emptyMainImageSlot = () => ({
  file: null,
  existingUrl: null,
  isChanged: false,
});

const LIST_KEYS = [
  "MainImages",
  "mainImages",
  "ItemMainImages",
  "itemMainImages",
  "MainImageList",
  "mainImageList",
  "MainImageUrls",
  "mainImageUrls",
  "itemImages",
  "ItemImages",
];

function asArray(value) {
  if (!value && value !== 0) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "null") return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return asArray(JSON.parse(trimmed));
      } catch {
        if (trimmed.includes(",")) {
          return trimmed.split(",").map((part) => part.trim()).filter(Boolean);
        }
        return [trimmed];
      }
    }
    if (trimmed.includes("/uploads/") && trimmed.includes(",")) {
      return trimmed.split(",").map((part) => part.trim()).filter(Boolean);
    }
    return [trimmed];
  }
  if (typeof value !== "object") return [];
  if (Array.isArray(value.$values)) return value.$values;
  if (Array.isArray(value.values)) return value.values;
  if (Array.isArray(value.Items)) return value.Items;
  if (Array.isArray(value.items)) return value.items;
  if (entryToImageUrl(value)) return [value];
  return [];
}

export function entryToImageUrl(entry) {
  if (!entry && entry !== 0) return "";
  if (typeof entry === "string") return entry.trim();
  if (typeof entry !== "object") return "";
  const url =
    entry.ImageUrl ||
    entry.imageUrl ||
    entry.ImageURL ||
    entry.Url ||
    entry.url ||
    entry.Path ||
    entry.path ||
    entry.FilePath ||
    entry.filePath ||
    entry.Image ||
    entry.image ||
    entry.MainImageUrl ||
    entry.mainImageUrl ||
    entry.existingUrl ||
    "";
  return String(url || "").trim();
}

export function extractMainImageUrls(item) {
  if (!item || typeof item !== "object") return [];

  const collected = [];

  LIST_KEYS.forEach((key) => {
    asArray(item[key]).forEach((entry) => {
      const url = entryToImageUrl(entry);
      if (url && url !== "null" && url !== "undefined") collected.push(url);
    });
  });

  const singles = [
    item.mainImageUrl,
    item.MainImageUrl,
    item.mainImage,
    item.MainImage,
  ];
  singles.forEach((single) => {
    const url = entryToImageUrl(single);
    if (url && url !== "null" && url !== "undefined") collected.push(url);
  });

  const unique = [];
  collected.forEach((url) => {
    if (!unique.includes(url)) unique.push(url);
  });

  return unique.slice(0, MAX_MAIN_IMAGES);
}

export function toRelativeUploadPath(url) {
  if (!url) return "";
  const value = String(url);
  if (value.startsWith("blob:") || value.startsWith("data:")) return "";
  const idx = value.indexOf("/uploads/");
  if (idx >= 0) return value.slice(idx);
  if (value.startsWith("uploads/")) return `/${value}`;
  return value.startsWith("/") ? value : "";
}

export function slotsFromImageUrls(urls) {
  const list = (urls || [])
    .map((url) => entryToImageUrl(url))
    .filter((url) => url && url !== "null" && url !== "undefined")
    .slice(0, MAX_MAIN_IMAGES)
    .map((url) => ({ file: null, existingUrl: url, isChanged: false }));
  return list.length ? list : [emptyMainImageSlot()];
}

export function getFormMainImageSlots(form) {
  if (Array.isArray(form?.mainImages) && form.mainImages.length) {
    return form.mainImages.slice(0, MAX_MAIN_IMAGES).map((slot) => {
      if (slot && typeof slot === "object" && ("existingUrl" in slot || "file" in slot || "isChanged" in slot)) {
        const url = entryToImageUrl(slot.existingUrl) || slot.existingUrl || null;
        return { ...slot, existingUrl: url };
      }
      const url = entryToImageUrl(slot);
      return { file: null, existingUrl: url || null, isChanged: false };
    });
  }
  return [form?.mainImage || emptyMainImageSlot()];
}

export function getCardGalleryImages(product, variantImage) {
  const mains = extractMainImageUrls(product);
  if (mains.length > 1) return mains;
  if (variantImage) return [variantImage];
  if (mains.length) return mains;
  const fallback = product?.mainImage || product?.mainImageUrl;
  return fallback ? [fallback] : [];
}

export function withMainImageSlots(form, slots) {
  const nextSlots = (slots || []).slice(0, MAX_MAIN_IMAGES);
  const safeSlots = nextSlots.length ? nextSlots : [emptyMainImageSlot()];
  return {
    ...form,
    mainImages: safeSlots,
    mainImage: safeSlots[0] || emptyMainImageSlot(),
  };
}
